import { useState, useCallback } from 'react';
import * as api from '../services/api';

const SUBSTRING_RESULT_LIMIT = 150;
const CODE_RESULT_LIMIT = 150;
const TEXT_RESULT_LIMIT = 150;
const RANKED_RESULT_LIMIT = 100;

function substringSignificantText(value) {
  return String(value || '').trim().replace(/[%_\s]+/g, '');
}

function hasSubstringFilterContext(filters = {}) {
  return Boolean(
    filters.yearFrom ||
    filters.yearTo ||
    filters.country ||
    filters.tradingCountry ||
    filters.trademark ||
    filters.importer ||
    filters.exporter ||
    filters.productCodePrefix
  );
}

function isSubstringQueryAllowed(query, filters = {}) {
  const significant = substringSignificantText(query);
  if (significant.length >= 4) return true;
  if (significant.length >= 3 && (/\d/.test(significant) || hasSubstringFilterContext(filters))) {
    return true;
  }
  return false;
}

function getWindowLimit(searchMode, searchQuery) {
  if (searchMode === 'ilike' || searchQuery.includes('%')) return SUBSTRING_RESULT_LIMIT;
  if (searchMode === 'exact') return CODE_RESULT_LIMIT;
  if (searchMode === 'fulltext') return TEXT_RESULT_LIMIT;
  return RANKED_RESULT_LIMIT;
}

/**
 * Main search state and API interaction hook.
 */
export function useSearch() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchInfo, setSearchInfo] = useState(null);
  const [totalEstimate, setTotalEstimate] = useState(0);
  const [filtersApplied, setFiltersApplied] = useState({});
  const [suggestion, setSuggestion] = useState(null);
  const [phrasingAlternatives, setPhrasingAlternatives] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState('auto');

  const performSearch = useCallback(async (searchQuery, searchMode = 'auto', filters = {}) => {
    if (!searchQuery || searchQuery.trim().length < 1) return;

    const substringRequest = searchMode === 'ilike' || searchQuery.includes('%');
    const effectivePage = 0;
    const effectiveLimit = getWindowLimit(searchMode, searchQuery);
    if (substringRequest && !isSubstringQueryAllowed(searchQuery, filters)) {
      setLoading(false);
      setError('Для поиска по подстроке введите минимум 4 символа. 3 символа допустимы только с цифрой или фильтром.');
      setQuery(searchQuery);
      setMode(searchMode);
      setCurrentPage(0);
      setResults([]);
      setSearchInfo(null);
      setTotalEstimate(0);
      setSuggestion(null);
      setPhrasingAlternatives([]);
      return;
    }

    setLoading(true);
    setError(null);
    setQuery(searchQuery);
    setMode(searchMode);
    setCurrentPage(effectivePage);

    try {
      const data = await api.search(
        searchQuery,
        searchMode,
        filters,
        effectiveLimit,
        0,
      );
      setResults(data.results || []);
      setSearchInfo(data.search_info || null);
      setTotalEstimate(data.total_estimate || 0);
      setFiltersApplied(data.filters_applied || {});
      setSuggestion(data.suggestion || null);
      setPhrasingAlternatives(Array.isArray(data.phrasing_alternatives) ? data.phrasing_alternatives : []);
    } catch (err) {
      setError(err.message || 'Ошибка поиска');
      setResults([]);
      setSuggestion(null);
      setPhrasingAlternatives([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const nextPage = useCallback((filters) => {
    performSearch(query, mode, filters, currentPage + 1);
  }, [performSearch, query, mode, currentPage]);

  const prevPage = useCallback((filters) => {
    if (currentPage > 0) {
      performSearch(query, mode, filters, currentPage - 1);
    }
  }, [performSearch, query, mode, currentPage]);

  const goToPage = useCallback((page, filters) => {
    performSearch(query, mode, filters, page);
  }, [performSearch, query, mode]);

  const clearResults = useCallback(() => {
    setResults([]);
    setSearchInfo(null);
    setTotalEstimate(0);
    setError(null);
    setSuggestion(null);
    setPhrasingAlternatives([]);
    setCurrentPage(0);
  }, []);

  return {
    results,
    loading,
    error,
    searchInfo,
    totalEstimate,
    filtersApplied,
    suggestion,
    phrasingAlternatives,
    currentPage,
    pageSize: getWindowLimit(mode, query),
    performSearch,
    nextPage,
    prevPage,
    goToPage,
    clearResults,
  };
}
